import { useEffect, useState } from "react";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Pencil, X, MapPin } from "lucide-react";

const empty = { code: "", name: "", color: "#4ade80", notes: "", active: true };

const SWATCHES = ["#4ade80", "#22c55e", "#fbbf24", "#f97316", "#f87171", "#a78bfa", "#60a5fa", "#94a3b8"];

export default function Locations() {
    const [locations, setLocations] = useState([]);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/locations");
            setLocations(asList(data, ["locations"]));
        } catch (e) {
            console.error("[locations] load failed", e);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        setError("");
        const code = (editing.code || "").trim();
        const name = (editing.name || "").trim();
        if (!code) return setError("Code is verplicht");
        if (!name) return setError("Naam is verplicht");
        try {
            const payload = { ...editing, code, name };
            if (editing.id) await api.put(`/locations/${editing.id}`, payload);
            else await api.post("/locations", payload);
            setEditing(null);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
        }
    };

    const remove = async (l) => {
        if (!window.confirm(`Locatie ${l.code} - ${l.name} verwijderen?`)) return;
        try {
            await api.delete(`/locations/${l.id}`);
            await load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || "Verwijderen mislukt");
        }
    };

    return (
        <div data-testid="locations-page">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-ink-900 flex items-center gap-2">
                    <MapPin className="h-5 w-5" /> Locaties
                </h2>
                <button
                    onClick={() => {
                        setError("");
                        setEditing({ ...empty });
                    }}
                    className="px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2"
                    data-testid="btn-new-location"
                >
                    <Plus className="h-4 w-4" /> Nieuwe locatie
                </button>
            </div>

            <p className="text-sm text-ink-500 mb-6 max-w-2xl mx-auto">
                Locaties zijn de fysieke plekken (kassen, zones, schuren) waar planten staan.
                Koppel ze in <em>Producten</em> aan elke plant, zodat orders, karren en bonnen
                automatisch per locatie gescheiden zijn.
            </p>

            <div className="bg-white border border-border rounded-md overflow-hidden">
                <table className="w-full text-sm" data-testid="locations-table">
                    <thead className="bg-ink-50">
                        <tr>
                            <th className="text-left px-4 py-3 data-label w-12">Kleur</th>
                            <th className="text-left px-4 py-3 data-label">Code</th>
                            <th className="text-left px-4 py-3 data-label">Naam</th>
                            <th className="text-left px-4 py-3 data-label">Notities</th>
                            <th className="text-left px-4 py-3 data-label">Actief</th>
                            <th className="px-4 py-3 w-24"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {locations.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-5 py-12 text-center text-ink-500">
                                    Nog geen locaties — maak je eerste aan.
                                </td>
                            </tr>
                        )}
                        {locations.map((l) => (
                            <tr
                                key={l.id}
                                className="border-t border-border/60 hover:bg-ink-50"
                                data-testid={`location-row-${l.code}`}
                            >
                                <td className="px-4 py-3">
                                    <span
                                        className="inline-block h-5 w-5 rounded-sm border border-ink-200"
                                        style={{ backgroundColor: l.color || "#e5e7eb" }}
                                    />
                                </td>
                                <td className="px-4 py-3 font-mono text-ink-900">{l.code}</td>
                                <td className="px-4 py-3 text-ink-900">{l.name}</td>
                                <td className="px-4 py-3 text-ink-500 text-xs">{l.notes || "—"}</td>
                                <td className="px-4 py-3 text-xs">{l.active ? "ja" : "nee"}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => {
                                            setError("");
                                            setEditing({ ...l });
                                        }}
                                        className="text-ink-700 hover:text-florefy-900 p-1"
                                        data-testid={`btn-edit-location-${l.code}`}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => remove(l)}
                                        className="text-ink-700 hover:text-destructive p-1 ml-1"
                                        data-testid={`btn-delete-location-${l.code}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-florefy-900/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-md w-full max-w-md p-6 mx-auto" data-testid="location-modal">
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                            <h2 className="text-xl font-semibold text-ink-900">
                                {editing.id ? "Locatie bewerken" : "Nieuwe locatie"}
                            </h2>
                            <button
                                onClick={() => setEditing(null)}
                                className="text-ink-700 hover:text-destructive"
                                data-testid="btn-close-location-modal"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        {error && (
                            <div className="bg-destructive/10 border border-destructive text-destructive text-sm p-3 mb-3 rounded-md">
                                {error}
                            </div>
                        )}
                        <div className="space-y-4">
                            <div>
                                <div className="data-label mb-1">Code (kort, bijv. A)</div>
                                <input
                                    className={inputCls}
                                    value={editing.code}
                                    onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                                    data-testid="input-location-code"
                                    placeholder="A, B, KAS-1..."
                                />
                            </div>
                            <div>
                                <div className="data-label mb-1">Naam</div>
                                <input
                                    className={inputCls}
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    data-testid="input-location-name"
                                    placeholder="Kas A — Lage potten"
                                />
                            </div>
                            <div>
                                <div className="data-label mb-1">Kleur (voor visuele groepering)</div>
                                <div className="flex flex-wrap gap-2">
                                    {SWATCHES.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => setEditing({ ...editing, color: c })}
                                            className={`h-7 w-7 rounded-sm border ${
                                                editing.color === c
                                                    ? "border-ink-900 ring-2 ring-florefy-900"
                                                    : "border-ink-200"
                                            }`}
                                            style={{ backgroundColor: c }}
                                            data-testid={`color-swatch-${c}`}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="data-label mb-1">Notities</div>
                                <textarea
                                    className={inputCls}
                                    rows="2"
                                    value={editing.notes}
                                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                    data-testid="input-location-notes"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-ink-900">
                                <input
                                    type="checkbox"
                                    checked={editing.active}
                                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                    data-testid="check-location-active"
                                />
                                Actief
                            </label>
                        </div>
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                            <button
                                onClick={() => setEditing(null)}
                                className="px-4 py-2 rounded-md border border-border text-sm hover:bg-ink-50"
                            >
                                Annuleer
                            </button>
                            <button
                                onClick={save}
                                className="px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm font-medium"
                                data-testid="btn-save-location"
                            >
                                Opslaan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 border border-border rounded-md bg-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-florefy-900 focus:border-florefy-900";
